import React from "react";
import { Button } from "@/components/ui/button";
import Banner from "@/components/ui/banner";
import {
  ServiceItemFormData,
  ChargeType,
  PricingModel,
  ServiceItem
} from "../types";
import { SECTIONS } from "../consts";
import { useServiceItemTypes, usePricingModelOptions } from "../hooks";
import { useServiceItemForm } from "../hooks/useServiceItemForm";
import { useCurrencySymbol } from "../hooks/useCurrencySymbol";
import Text from "@/components/ui/text";
import { Close } from "@dreamhub/designsystem/icons";
import { IconButton } from "@/components/ui/icon-button";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle
} from "@/components/ui/drawer";
import { AppDrawerContent } from "@/components/layouts/AppDrawerContent";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import { FieldType } from "@/components/controls/Form/types";
import PricingTable from "./PricingTable";
import { useTranslation } from "react-i18next";

const TRANSLATION_KEY = "settings.product";

interface ServiceItemDrawerProps {
  isOpen: boolean;
  productId: string;
  serviceItem?: ServiceItem | null;
  onClose: () => void;
  onSave: (data: ServiceItemFormData) => void;
  onRefetchProducts?: () => void;
}

const ServiceItemDrawer: React.FC<ServiceItemDrawerProps> = ({
  isOpen,
  productId,
  serviceItem,
  onClose,
  onRefetchProducts
}) => {
  const { t } = useTranslation();
  const currencySymbol = useCurrencySymbol();
  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  const { data: pricingModelOptions = [] } = usePricingModelOptions();

  const {
    formState,
    isFormValid,
    formRef,
    setFormSetValue,
    handleFormValid,
    handleFormSubmit,
    handleSubmitButton,
    isSubmitting,
    setPricing
  } = useServiceItemForm({
    serviceItem,
    productId,
    isEditMode: !!serviceItem
  });

  const createTypeOptions = () => {
    const options: any[] = [];

    SECTIONS.forEach((section) => {
      const sectionTypes = serviceItemTypes.filter(
        (type) => type.sectionId === section.id
      );

      sectionTypes.forEach((type) => {
        options.push({
          id: type.id,
          displayName: type.name,
          section: section.id.toString()
        });
      });
    });

    return options;
  };

  const typeOptions = createTypeOptions();

  const sections: FormSectionProps[] = React.useMemo(
    () => [
      {
        id: "serviceItemInfo",
        divider: false,
        fields: [
          {
            name: "typeId",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.type`),
            type: "singleSelect",
            required: true,
            defaultValue: !serviceItem ? "" : undefined,
            fieldActionProps: {
              placeholder: t(
                `${TRANSLATION_KEY}.serviceItemDrawer.typePlaceholder`
              ),
              options: typeOptions,
              showSelectedItemSubtitle: false,
              enableSections: true,
              sections: SECTIONS.map((section) => ({
                id: section.id.toString(),
                title: t(section.name)
              }))
            }
          },
          {
            name: "name",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.nameSku`),
            type: "text",
            required: true,
            defaultValue: !serviceItem ? "" : undefined,
            fieldActionProps: {
              placeholder: t(
                `${TRANSLATION_KEY}.serviceItemDrawer.namePlaceholder`
              )
            },
            subLabel: t(`${TRANSLATION_KEY}.serviceItemDrawer.nameSubLabel`)
          },
          {
            name: "description",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.description`),
            type: "textarea",
            required: false,
            defaultValue: !serviceItem ? "" : undefined,
            fieldActionProps: {
              placeholder: t(
                `${TRANSLATION_KEY}.serviceItemDrawer.descriptionPlaceholder`
              ),
              minRows: 1
            },
            fieldClassName: "border-b-[0.5px] border-border-border1 pb-4"
          },
          {
            name: "chargeType",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.chargeType`),
            type: "tabs",
            required: true,
            defaultValue: !serviceItem
              ? String(ChargeType.RECURRING)
              : undefined,
            fieldActionProps: {
              options: {
                [String(ChargeType.RECURRING)]: t(
                  `${TRANSLATION_KEY}.chargeType.recurring`
                ),
                [String(ChargeType.ONE_TIME)]: t(
                  `${TRANSLATION_KEY}.chargeType.oneTime`
                )
              },
              fullWidth: true
            }
          },
          {
            name: "pricingModel",
            label: t(`${TRANSLATION_KEY}.serviceItemDrawer.pricingModel`),
            type: "singleSelect",
            required: true,
            defaultValue: !serviceItem ? PricingModel.PER_UNIT : undefined,
            fieldActionProps: {
              placeholder: t(`${TRANSLATION_KEY}.serviceItemDrawer.select`),
              options: pricingModelOptions.map((option) => ({
                id: option.id,
                displayName: option.name,
                subtitle: (
                  <Text variant="smBodySecondary">{option.description}</Text>
                ),
                isHidden: false,
                disabled: !option.isEnabled,
                comingSoon: !option.isEnabled
              })),
              showSelectedItemSubtitle: false
            },
            subLabel: t(
              `${TRANSLATION_KEY}.serviceItemDrawer.pricingModelSubLabel`
            )
          },
          ...(Number(formState.chargeType) === ChargeType.ONE_TIME &&
          formState.formData.pricingModel !== PricingModel.FLEXIBLE
            ? [
                {
                  name: "oneTimePrice",
                  label: t(`${TRANSLATION_KEY}.serviceItemDrawer.price`),
                  type: "number" as FieldType,
                  required: true,
                  fieldActionProps: {
                    placeholder: t(
                      `${TRANSLATION_KEY}.serviceItemDrawer.pricePlaceholder`
                    ),
                    trailingIcon: (
                      <Text variant="body" className="text-gray-500">
                        {currencySymbol}
                      </Text>
                    )
                  }
                }
              ]
            : [])
        ]
      }
    ],
    [
      serviceItem,
      formState.chargeType,
      formState.formData.pricingModel,
      typeOptions,
      currencySymbol,
      t,
      serviceItemTypes,
      pricingModelOptions
    ]
  );

  const handleSave = async () => {
    try {
      await handleFormSubmit();
      onRefetchProducts?.();
      onClose();
    } catch (error) {
      console.error("Failed to save service item:", error);
    }
  };

  return (
    <Drawer
      open={isOpen}
      direction="right"
      fixed
      modal={false}
      disablePreventScroll={false}
      noBodyStyles={true}
      dismissible={false}
    >
      <AppDrawerContent
        className="p-0 overflow-x-hidden w-full"
        data-test="products-service-drawer"
        isSettingsDrawer
      >
        <DrawerHeader>
          <DrawerTitle>
            <Text
              variant="lgSubtitle"
              data-test="products-service-drawer-title"
            >
              {serviceItem
                ? t(`${TRANSLATION_KEY}.serviceItemDrawer.editService`)
                : t(`${TRANSLATION_KEY}.serviceItemDrawer.addService`)}
            </Text>
          </DrawerTitle>
          <IconButton
            onClick={onClose}
            variant="iconNoBg"
            size="sm"
            data-test="products-service-drawer-close-button"
          >
            <Close />
          </IconButton>
        </DrawerHeader>

        <div className="px-5 py-5 w-full overflow-y-scroll">
          <FormNew
            sections={sections}
            isFormValid={handleFormValid}
            onSubmit={handleSave}
            formRef={formRef}
            onSetValue={setFormSetValue}
            key={`service-item-form-${serviceItem?.id || "new"}`}
          />

          {formState.formData.pricingModel === PricingModel.FLEXIBLE ? (
            <Banner
              variant="info"
              title={t(
                `${TRANSLATION_KEY}.serviceItemDrawer.flexiblePricingInfo`
              )}
              showCloseButton={false}
              titleVariant="body"
            />
          ) : Number(formState.chargeType) === ChargeType.RECURRING ? (
            <div className="mt-2 pt-6">
              <PricingTable
                pricing={formState.pricing}
                onPricingChange={setPricing}
                chargeType={formState.chargeType}
              />
            </div>
          ) : null}
        </div>

        <DrawerFooter>
          <div className="flex justify-end gap-2 w-full p-4 border-t-[0.5px] border-border-border1">
            <Button
              variant="secondaryNoBg"
              onClick={onClose}
              data-test="products-service-drawer-cancel-button"
            >
              {t(`${TRANSLATION_KEY}.serviceItemDrawer.cancel`)}
            </Button>
            <Button
              disabled={!isFormValid || isSubmitting}
              onClick={handleSubmitButton}
              data-test="products-service-drawer-submit-button"
            >
              {t(`${TRANSLATION_KEY}.serviceItemDrawer.addToProduct`)}
            </Button>
          </div>
        </DrawerFooter>
      </AppDrawerContent>
    </Drawer>
  );
};

export default ServiceItemDrawer;
